import React, { useState, useRef, useEffect } from 'react'

/**
 * Formats message content with markdown-like styling
 */
function formatMessage(content) {
  if (!content) return ''
  
  return content
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Bullet points
    .replace(/^• (.*$)/gim, '<li>$1</li>')
    // Headers
    .replace(/^#{3} (.*$)/gim, '<h3>$1</h3>')
    .replace(/^#{2} (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    // Line breaks
    .replace(/\n/g, '<br />')
}

/**
 * Individual message bubble component
 */
function ChatMessage({ message, isLatest }) {
  const isUser = message.role === 'user'
  const isSystem = message.role === 'system'
  const isStreaming = message.isStreaming
  
  if (isSystem) {
    return (
      <div className="flex justify-center my-2">
        <div className={`px-3 py-1 rounded-full text-xs ${message.isError ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
          {message.content}
        </div>
      </div>
    )
  }
  
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      {!isUser && (
        <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center mr-2 flex-shrink-0">
          <span className="text-white text-sm">🤖</span>
        </div>
      )}
      
      <div
        className={`max-w-[85%] px-4 py-2 rounded-2xl text-sm ${
          isUser
            ? 'bg-primary-600 text-white rounded-br-md'
            : 'bg-gray-100 text-gray-800 rounded-bl-md'
        } ${isStreaming ? 'animate-pulse' : ''}`}
      >
        <div 
          className="prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ 
            __html: formatMessage(message.content) 
          }}
        />
        {isStreaming && (
          <span className="inline-block ml-1 animate-pulse">▌</span>
        )}
      </div>
      
      {isUser && (
        <div className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center ml-2 flex-shrink-0">
          <span className="text-white text-sm">👤</span>
        </div>
      )}
    </div>
  )
}

/**
 * Typing indicator component
 */
function TypingIndicator() {
  return (
    <div className="flex justify-start mb-3">
      <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center mr-2 flex-shrink-0">
        <span className="text-white text-sm">🤖</span>
      </div>
      <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
        <div className="flex gap-1">
          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
        </div>
      </div>
    </div>
  )
}

/**
 * Main AgentChat component
 */
function AgentChat({ 
  messages, 
  isConnected, 
  isConnecting, 
  isStreaming, 
  error, 
  onSendMessage,
  onReconnect,
  analysisComplete 
}) {
  const [inputValue, setInputValue] = useState('')
  const [isExpanded, setIsExpanded] = useState(true)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  
  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, isStreaming])
  
  // Focus input when expanded
  useEffect(() => {
    if (isExpanded && inputRef.current && analysisComplete) {
      inputRef.current.focus()
    }
  }, [isExpanded, analysisComplete])
  
  const handleSubmit = (e) => {
    e.preventDefault()
    if (!inputValue.trim() || isStreaming) return
    
    onSendMessage(inputValue.trim())
    setInputValue('')
  }
  
  // Quick question buttons
  const quickQuestions = [
    "Why this score?",
    "Explain Monte Carlo",
    "Main risks?",
    "Best strategy?"
  ]
  
  const handleQuickQuestion = (question) => {
    if (isStreaming) return
    
    // Expand full question based on button
    const fullQuestions = {
      "Why this score?": "Can you explain why the agents gave this property this particular investment score?",
      "Explain Monte Carlo": "Please explain what the Monte Carlo simulation results mean for this investment.",
      "Main risks?": "What are the main risks I should be aware of with this property?",
      "Best strategy?": "Which investment strategy is best for this property and why?"
    }
    
    onSendMessage(fullQuestions[question] || question)
  }
  
  // Show collapsed state
  if (!isExpanded) {
    return (
      <div 
        className="border-t border-gray-200 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={() => setIsExpanded(true)}
      >
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">🤖</span>
            <span className="font-semibold text-gray-700">Ask the AI Agent</span>
            {isConnected && (
              <span className="w-2 h-2 bg-green-500 rounded-full" title="Connected"></span>
            )}
          </div>
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    )
  }
  
  return (
    <div className="border-t border-gray-200 bg-gray-50 flex flex-col">
      {/* Header */}
      <div 
        className="flex items-center justify-between px-4 py-2 bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors"
        onClick={() => setIsExpanded(false)}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">🤖</span>
          <span className="font-semibold text-gray-700">AI Agent Chat</span>
          {isConnected ? (
            <span className="w-2 h-2 bg-green-500 rounded-full" title="Connected"></span>
          ) : isConnecting ? (
            <span className="text-xs text-gray-500">(connecting...)</span>
          ) : (
            <button 
              onClick={(e) => {
                e.stopPropagation()
                onReconnect?.()
              }}
              className="text-xs text-red-600 hover:text-red-800 underline"
              title="Click to reconnect"
            >
              (disconnected - click to reconnect)
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!analysisComplete && (
            <span className="text-xs text-gray-500">Analysis in progress...</span>
          )}
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </div>
      </div>
      
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-3 py-2 max-h-64 min-h-48">
        {messages.length === 0 ? (
          <div className="text-center text-gray-400 py-8">
            <div className="text-3xl mb-2">🤖</div>
            <p className="text-sm">Analysis complete!</p>
            <p className="text-xs mt-1">Ask me anything about this property</p>
          </div>
        ) : (
          <>
            {messages.map((message, idx) => (
              <ChatMessage 
                key={idx} 
                message={message} 
                isLatest={idx === messages.length - 1}
              />
            ))}
            {isStreaming && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </>
        )}
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-2 mt-2">
            <p className="text-xs text-red-600">{error}</p>
          </div>
        )}
      </div>
      
      {/* Quick Questions */}
      {analysisComplete && !isStreaming && messages.length > 0 && messages.length < 3 && (
        <div className="px-3 pb-2">
          <p className="text-xs text-gray-500 mb-2">Quick questions:</p>
          <div className="flex flex-wrap gap-2">
            {quickQuestions.map((q) => (
              <button
                key={q}
                onClick={() => handleQuickQuestion(q)}
                className="px-2 py-1 bg-white border border-gray-200 rounded-full text-xs text-gray-600 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-700 transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Input Area */}
      <form onSubmit={handleSubmit} className="p-3 border-t border-gray-200 bg-white">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={analysisComplete ? "Ask about the analysis..." : "Waiting for analysis..."}
            disabled={!analysisComplete || isStreaming}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-400"
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || isStreaming || !analysisComplete}
            className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {isStreaming ? (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            )}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-1 text-center">
          AI responses are for informational purposes only
        </p>
      </form>
    </div>
  )
}

export default AgentChat
